import { useF0FormDefinition } from '@factorialco/f0-react'
import i18n from 'modules/core/lib/i18n'
import { useMultiTeamSelectorFormField } from 'modules/core/lib/formFields/teamSelector'
import { useMultiLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'
import { useSinglePayrollConceptSelectorFormField } from 'modules/incentives/lib/formFields/payrollConceptSelector'
import {
  createBasicsSchema,
  type BasicsStepData,
} from 'modules/incentives/components/CreateCompensationPlanDialog/steps/basics/schema'
import {
  PayrollValueObjectsConceptCategoryEnum,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'

interface UseCreateCompensationPlanFormProps {
  onSubmit: (data: BasicsStepData) => Promise<boolean>
}

const ADDITIONAL_COMPENSATION_CATEGORIES: PayrollValueObjectsConceptCategoryEnum[] = [
  'earnings_variable',
]
const ADDITIONAL_COMPENSATION_UNITS: PayrollValueObjectsConceptUnitTypeEnum[] = ['money']

export const useCreateCompensationPlanForm = ({ onSubmit }: UseCreateCompensationPlanFormProps) => {
  const payrollConceptId = useSinglePayrollConceptSelectorFormField({
    label: i18n.t('incentives.wizard.basics.payroll_concept.label'),
    placeholder: i18n.t('incentives.wizard.basics.payroll_concept.placeholder'),
    helpText: i18n.t('incentives.wizard.basics.payroll_concept.helpText'),
    mandatory: false,
    categories: ADDITIONAL_COMPENSATION_CATEGORIES,
    unitTypes: ADDITIONAL_COMPENSATION_UNITS,
    withActiveStatus: true,
  })

  const teamIds = useMultiTeamSelectorFormField({
    label: i18n.t('incentives.wizard.basics.teams.label'),
    mandatory: false,
    renderIf: {
      fieldId: 'filterScope',
      equalsTo: 'team',
    },
    row: 'filter-scope-row',
  })

  const legalEntityIds = useMultiLegalEntitySelectorFormField({
    label: i18n.t('incentives.wizard.basics.legal_entities.label'),
    mandatory: false,
    renderIf: {
      fieldId: 'filterScope',
      equalsTo: 'legal_entity',
    },
    row: 'filter-scope-row',
  })

  const basicsSchema = createBasicsSchema({
    payrollConceptId,
    teamIds,
    legalEntityIds,
  })

  const formDefinition = useF0FormDefinition({
    name: 'incentives-new-compensation-plan',
    schema: basicsSchema,
    defaultValues: {
      name: i18n.t('incentives.wizard.basics.name.defaultValue'),
      recurrence: 'every_12_months' as const,
      filterScope: 'all_company' as const,
      startsOnMonthly: new Date(),
      startsOnQuarterly: new Date(),
      startsOnSemiAnnual: new Date(),
      startsOnAnnual: new Date(),
    },
    onSubmit: async ({ data }) => {
      const submitted = await onSubmit(data)
      return { success: submitted }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return { formDefinition }
}
